import axios from 'axios';

// Configuração base da API
// Em produção, defina VITE_API_URL como a URL base da API (com ou sem /api).
// Em desenvolvimento local, usamos http://localhost:5000/api como padrão.
// Este código garante que o sufixo /api seja adicionado apenas uma vez.
const rawBaseUrl = import.meta.env.VITE_API_URL;

const normalizeApiBaseUrl = (url) => {
  if (!url) return '';
  const trimmed = url.replace(/\/+$/, '');
  if (trimmed.endsWith('/api')) {
    return trimmed;
  }
  return `${trimmed}/api`;
};

const API_BASE_URL = normalizeApiBaseUrl(rawBaseUrl);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requisições
api.interceptors.request.use(
  (config) => {
    // Adicionar token de autenticação se disponível
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para respostas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Tratar erros de autenticação
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    // Tratar erros de rede
    if (!error.response) {
      error.message = 'Erro de conexão. Verifique sua internet.';
    }
    
    return Promise.reject(error);
  }
);

// Serviços da API
const isFormData = (data) =>
  typeof FormData !== 'undefined' && data instanceof FormData;

export const vehicleService = {
  // Buscar todos os veículos
  getAllVehicles: async (filters = {}) => {
    try {
      const response = await api.get('/vehicles', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar veículos');
    }
  },

  // Buscar veículo por ID
  getVehicleById: async (id) => {
    try {
      const response = await api.get(`/vehicles/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar veículo');
    }
  },

  // Buscar veículos em destaque
  getFeaturedVehicles: async (limit = 6) => {
    try {
      const response = await api.get('/vehicles/featured', { 
        params: { limit } 
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar veículos em destaque');
    }
  },

  // Buscar veículos por marca
  getVehiclesByBrand: async (brand) => {
    try {
      const response = await api.get(`/vehicles/brand/${brand}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar veículos por marca');
    }
  },

  // Buscar estatísticas
  getStatistics: async () => {
    try {
      const response = await api.get('/vehicles/statistics');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar estatísticas');
    }
  },

  // Buscar sugestões de características de veículos
  getFeatureSuggestions: async () => {
    try {
      const response = await api.get('/vehicles/features');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar características de veículos');
    }
  },

  // Criar veículo (admin)
  createVehicle: async (vehicleData) => {
    try {
      // Para FormData, garantir header multipart; para JSON, usar default
      const config = isFormData(vehicleData)
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};
      const response = await api.post('/vehicles', vehicleData, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao criar veículo');
    }
  },

  // Atualizar veículo (admin)
  updateVehicle: async (id, vehicleData) => {
    try {
      // Para FormData, garantir header multipart; para JSON, usar default
      const config = isFormData(vehicleData)
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};
      const response = await api.put(`/vehicles/${id}`, vehicleData, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar veículo');
    }
  },

  // Deletar veículo (admin)
  deleteVehicle: async (id) => {
    try {
      const response = await api.delete(`/vehicles/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao deletar veículo');
    }
  },

  // Atualizar apenas a ordem de exibição do veículo (admin)
  updateVehicleDisplayOrder: async (id, displayOrder) => {
    try {
      const response = await api.patch(`/vehicles/${id}/display-order`, {
        display_order: displayOrder
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar ordem do veículo');
    }
  }
};

// Serviços de Cliente
export const customerService = {
  // Buscar todos os clientes
  getAllCustomers: async (filters = {}) => {
    try {
      const response = await api.get('/customers', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar clientes');
    }
  },

  // Buscar cliente por ID
  getCustomerById: async (id) => {
    try {
      const response = await api.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar cliente');
    }
  },

  // Criar cliente
  createCustomer: async (customerData) => {
    try {
      const response = await api.post('/customers', customerData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao criar cliente');
    }
  },

  // Atualizar cliente
  updateCustomer: async (id, customerData) => {
    try {
      const response = await api.put(`/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar cliente');
    }
  },

  // Deletar cliente
  deleteCustomer: async (id) => {
    try {
      const response = await api.delete(`/customers/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao deletar cliente');
    }
  },

  // Buscar estatísticas de clientes
  getCustomerStatistics: async () => {
    try {
      const response = await api.get('/customers/statistics');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar estatísticas de clientes');
    }
  }
};

// Serviços de Venda
export const saleService = {
  // Buscar todas as vendas
  getAllSales: async (filters = {}) => {
    try {
      const response = await api.get('/sales', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar vendas');
    }
  },

  // Buscar venda por ID
  getSaleById: async (id) => {
    try {
      const response = await api.get(`/sales/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar venda');
    }
  },

  // Criar venda
  createSale: async (saleData) => {
    try {
      const response = await api.post('/sales', saleData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao criar venda');
    }
  },

  // Atualizar venda
  updateSale: async (id, saleData) => {
    try {
      const response = await api.put(`/sales/${id}`, saleData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar venda');
    }
  },

  // Deletar venda
  deleteSale: async (id) => {
    try {
      const response = await api.delete(`/sales/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao deletar venda');
    }
  },

  // Buscar estatísticas de vendas
  getSaleStatistics: async () => {
    try {
      const response = await api.get('/sales/statistics');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar estatísticas de vendas');
    }
  }
};

// Serviço de contato
export const contactService = {
  // Enviar mensagem de contato
  sendMessage: async (messageData) => {
    try {
      const response = await api.post('/contact', messageData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao enviar mensagem');
    }
  }
};

// Utilitários
export const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

export const formatMileage = (mileage) => {
  return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
};

export const formatYear = (year) => {
  return year.toString();
};

export default api;
